'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  useCarousel,
} from '@lastbrand/web-ui/carousel'
import { ChevronLeftIcon, ChevronRightIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Image } from '@src/quince-components/src/atoms'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { isProductImage } from '@src/quince-site-common/src/utils/typeGuards'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { useProductImages } from '../productImages/hooks/use-product-images'

const DRAG_THRESHOLD_PX = 10

const containerClass = css({
  width: '100%',
  marginTop: 'space-20',
  marginBottom: 'space-16',
  lg: { width: '50%', marginTop: 'space-32', marginBottom: '0' },
})

const ARROW_ICON_SIZE = '20px'

const carouselCss = css.raw({ position: 'relative' })

const contentCss = css.raw({
  gap: 'space-8',
  '& [data-slot="carousel-item"]': {
    minWidth: '0',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '310px',
    lg: { flexBasis: 'calc((100% - var(--web-ui-spacing-space-8)) / 2)' },
  },
})

const slideClass = css({
  cursor: 'pointer',
  width: '100%',
  aspectRatio: '4/5',
  padding: 0,
  border: 0,
  bg: 'transparent',
})

const carouselControlButtonCss = css.raw({
  position: 'absolute',
  top: '50%',
  zIndex: 1,
  display: { base: 'none', lg: 'inline-flex' },
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'space-8',
  borderRadius: 'radius-round',
  bg: 'white',
  boxShadow: 'shadow-below',
  color: 'darkgray',
  transition: 'color 150ms ease-out, background-color 150ms ease-out',
  _hover: { bg: 'paperwhite' },
  _active: { bg: 'offwhite' },
  _focusVisible: {
    outlineWidth: '2px',
    outlineOffset: '0.25rem',
    outlineColor: 'black',
    outlineStyle: 'solid',
  },
})

const prevArrowCss = css.raw(carouselControlButtonCss, { left: '0', transform: 'translate(-50%, -50%)' })
const nextArrowCss = css.raw(carouselControlButtonCss, { right: '0', transform: 'translate(50%, -50%)' })

function MiniPDPCarouselControls() {
  const { t } = useTranslation('common')
  const { canScrollPrev, canScrollNext } = useCarousel()

  return (
    <>
      {canScrollPrev && (
        <CarouselPreviousButton asChild css={prevArrowCss} aria-label={t('previous')}>
          <button type="button">
            <ChevronLeftIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
          </button>
        </CarouselPreviousButton>
      )}
      {canScrollNext && (
        <CarouselNextButton asChild css={nextArrowCss} aria-label={t('next')}>
          <button type="button">
            <ChevronRightIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
          </button>
        </CarouselNextButton>
      )}
    </>
  )
}

export const MiniPDPProductImagesDS = ({
  product,
  sectionData,
}: {
  product: Product
  sectionData: TPDPSectionData
}) => {
  const { t } = useTranslation('common')
  const { productMedia, selectedOptions } = useProductImages(product, sectionData)
  const productImages = productMedia.filter(media => isProductImage(media))
  const router = useRouter()

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  const productLink = getProductLink(product, selectedOptions)

  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)

  const handlePointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    draggedRef.current = false
  }, [])

  const handlePointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    if (!start) return
    if (
      Math.abs(event.clientX - start.x) > DRAG_THRESHOLD_PX ||
      Math.abs(event.clientY - start.y) > DRAG_THRESHOLD_PX
    ) {
      draggedRef.current = true
    }
  }, [])

  const handleImageClick = useCallback(() => {
    if (draggedRef.current) {
      draggedRef.current = false
      return
    }
    router.push(productLink)
  }, [router, productLink])

  return (
    <div className={containerClass}>
      <Carousel
        css={carouselCss}
        opts={{ align: 'start', containScroll: 'trimSnaps' }}
        onPointerDownCapture={handlePointerDownCapture}
        onPointerMoveCapture={handlePointerMoveCapture}
      >
        <CarouselContent css={contentCss}>
          {productImages.map((productImage, index) => (
            <CarouselItem key={productImage.image.id}>
              <button
                type="button"
                className={slideClass}
                onClick={handleImageClick}
                aria-label={t('view_product_details')}
              >
                <Image
                  {...ProductImage.makeImageProps(productImage)}
                  loading={isMobile ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 || index === 1 ? 'high' : 'auto'}
                  alt={productImage.image.title}
                />
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
        <MiniPDPCarouselControls />
      </Carousel>
    </div>
  )
}
